import React, { useEffect, useState, useCallback } from "react";
import { auditLogService } from "../services/api";
import { History, Search, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";

const ENTITY_TYPES = ["company", "director", "shareholder", "entity", "relationship", "document"];

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (entityTypeFilter) params.entity_type = entityTypeFilter;
      
      const response = await auditLogService.getAll(params);
      setLogs(response.data.items || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error("Failed to fetch audit logs");
    } finally {
      setLoading(false);
    }
  }, [page, entityTypeFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPage(1);
  }, [entityTypeFilter]);

  const getActionColor = (action) => {
    switch (action) {
      case 'create':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'update':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'delete':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="audit-logs-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Audit Logs</h1>
        <p className="text-sm text-slate-500 mt-1">Track all system changes</p>
      </div>

      {/* Filters */}
      <div className="enterprise-card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={entityTypeFilter || "all"} onValueChange={(v) => setEntityTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]" data-testid="entity-type-filter">
              <SelectValue placeholder="All Entity Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entity Types</SelectItem>
              {ENTITY_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="enterprise-card">
        {loading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded"></div>
              ))}
            </div>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No audit logs found</p>
            <p className="text-sm text-slate-400 mt-1">Changes will be recorded here</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Entity Type</th>
                    <th className="hidden sm:table-cell">Entity ID</th>
                    <th>Changed By</th>
                    <th>Date/Time</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} data-testid={`audit-log-${log.id}`}>
                      <td>
                        <span className={`badge ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td>{log.entity_type}</td>
                      <td className="hidden sm:table-cell font-mono text-xs">{log.entity_id?.substring(0, 8)}...</td>
                      <td>{log.changed_by_name || 'System'}</td>
                      <td className="text-xs">{log.changed_at}</td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                          data-testid={`view-log-${log.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Log Detail Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Log Details</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Action</p>
                  <span className={`badge mt-1 ${getActionColor(selectedLog.action)}`}>
                    {selectedLog.action}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Entity Type</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedLog.entity_type}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Entity ID</p>
                  <p className="text-sm text-slate-900 mt-1 font-mono">{selectedLog.entity_id}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Changed By</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedLog.changed_by_name || 'System'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-semibold uppercase text-slate-500">Date/Time</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedLog.changed_at}</p>
                </div>
              </div>
              
              {selectedLog.old_value && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Previous Value</p>
                  <pre className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.old_value, null, 2)}
                  </pre>
                </div>
              )}
              
              {selectedLog.new_value && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-2">New Value</p>
                  <pre className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.new_value, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
